/** Audit trail (M13-05): who did what, when, from where — read-only. */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button, Card, EmptyState, FilterBar, Modal, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Row {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  ip: string | null;
  at: string;
  user: { name: string; email: string } | null;
}

const ENTITIES = ['company', 'official', 'membership', 'role', 'user', 'stored_file'];

export function AuditLogPage(): JSX.Element {
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Row | null>(null);

  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (entity) params.set('entity', entity);
  if (action.trim()) params.set('action', action.trim());
  if (from) params.set('from', from);
  if (to) params.set('to', to);

  const log = useQuery({
    queryKey: ['audit', params.toString()],
    queryFn: () => api<{ data: Row[]; total: number; pageSize: number }>(`/audit-log?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const pages = log.data ? Math.max(1, Math.ceil(log.data.total / log.data.pageSize)) : 1;

  return (
    <>
      <PageHeader title="Audit Log" description="Every change, sign-in, lock and approval. Entries cannot be edited or deleted by anyone." />
      <FilterBar>
        <select className={filterClass} value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} aria-label="Record type">
          <option value="">All records</option>
          {ENTITIES.map((e) => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
        </select>
        <input className={`${filterClass} w-44`} placeholder="Action, e.g. UPDATED" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} aria-label="Action" />
        <input type="date" className={filterClass} value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label="From" />
        <input type="date" className={filterClass} value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label="To" />
      </FilterBar>
      <Card>
        {log.isLoading && <Skeleton className="h-64 w-full" />}
        {log.data?.data.length === 0 && <EmptyState title="Nothing matches" />}
        {log.data && log.data.data.length > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Record</Th>
                  <Th>Reason</Th>
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {log.data.data.map((r) => (
                  <tr key={r.id}>
                    <Td className="whitespace-nowrap text-slate-500">{dateTime(r.at)}</Td>
                    <Td className="whitespace-nowrap">{r.user?.name ?? 'System'}</Td>
                    <Td className="whitespace-nowrap font-medium">{r.action.replace(/_/g, ' ').toLowerCase()}</Td>
                    <Td className="whitespace-nowrap">{r.entity.replace(/_/g, ' ')}</Td>
                    <Td className="max-w-xs truncate">{r.reason ?? ''}</Td>
                    <Td align="right">{(r.before || r.after) ? <Button size="sm" variant="ghost" onClick={() => setOpen(r)}>Details</Button> : null}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="mt-4 flex items-center justify-end gap-2 text-sm text-slate-500">
              <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page} of {pages}</span>
              <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </Card>
      {open && (
        <Modal title={open.action.replace(/_/g, ' ').toLowerCase()} description={`${dateTime(open.at)} · ${open.user?.email ?? 'System'} · ${open.ip ?? ''}`} onClose={() => setOpen(null)}>
          <div className="grid gap-3 text-xs">
            {open.before !== null && open.before !== undefined && (
              <div><p className="mb-1 font-semibold">Before</p><pre className="max-h-60 overflow-auto rounded-lg bg-slate-100 p-3 dark:bg-slate-800">{JSON.stringify(open.before, null, 2)}</pre></div>
            )}
            {open.after !== null && open.after !== undefined && (
              <div><p className="mb-1 font-semibold">After</p><pre className="max-h-60 overflow-auto rounded-lg bg-slate-100 p-3 dark:bg-slate-800">{JSON.stringify(open.after, null, 2)}</pre></div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
